import React, { Fragment } from "react";
import { Link } from "react-router-dom";

import { Helmet } from "react-helmet";

import NavbarB from "../components/navbar-b";
import FooterB from "../components/footer-b";
import "./intro.css";

const Intro = (props) => {
  return (
    <div className="intro-container">
      <Helmet>
        <title>Intro - FF Season 2026</title>
        <meta property="og:title" content="Intro - FF Season 2026" />
        <link rel="canonical" href="https://feelers-feelers.com/intro" />
      </Helmet>
      <div className="intro-bg-container">
        <img src="/machineyearning/MY-main-bg.jpg" className="intro-image-bg" />
      </div>
      <div className="intro-navbar-container">
        <NavbarB rootClassName="navbar-broot-class-name4"></NavbarB>
      </div>
      <div className="intro-body">
        <div className="intro-body-bg">
          <img
            src="/machineyearning/MY-intro-01.webp"
            loading="eager"
            className="intro-image-1"
          />
          <img
            src="/machineyearning/MY-intro-02.webp"
            loading="eager"
            className="intro-image-2"
          />
          <img
            src="/machineyearning/MY-intro-03.webp"
            loading="eager"
            className="intro-image-3"
          />
          <img
            src="/machineyearning/MY-intro-04.webp"
            loading="eager"
            className="intro-image-4"
          />
          <img
            src="/machineyearning/MY-intro-05.webp"
            loading="eager"
            className="intro-image-5"
          />
          <img
            src="/machineyearning/MY-intro-06.webp"
            loading="eager"
            className="intro-image-6"
          />
        </div>
        <div className="intro-body-back MY-Subtitle">
          <Link to="/">&lt; BACK</Link>
        </div>
        <div className="intro-body-content">
          <span className="intro-text-title MY-Headline">INTRO</span>
          <div className="body-R MY-Body">
            <span>
              Dear you,
              <br></br>
              <br></br>
              As a teenager, I poured myself out to strangers and friends over
              instant message. I confessed crushes, picked fights, spiralled
              into slumps then clawed my way back out. Even when writing is
              hard, texting comes easily to me. Incomplete sentences, staccato.
              Lags between messages as poetic enjambment. Each an attempt to
              intimate something true within the short form’s constraints. 
              <br></br>
              <br></br>
              So I hope you don’t mind that it helps me to imagine you reading
              this. You: hopefully kind, curious, patient. Someone who, like me,
              learned to read warmth into writing, to imagine the emotional
              context that text doesn’t quite transmit.
            </span>
          </div>
          <div className="body-L MY-Body">
            <span>
              Dear you,
              <br></br>
              <br></br>
              Which technologies take off, and which stay left behind? That’s a
              question entwined with another question: what do we want from our
              machines? Our technologies habituate us, weaving themselves into
              the intimate fabric of our desires.
              <br></br>
              <br></br>
              In 2022, at the tail end of the pandemic, OpenAI launched ChatGPT.
              It was based on GPT-3, a large language model (LLM) I’d heard
              nothing about even though it had been released two years earlier.
              What was new was the chat interface, powered by machine learning
              and natural language processing. 
              <br></br>
              <br></br>
              Coming of age on the internet, I already understood text chats as
              spaces of intimacy. But my early interactions with ChatGPT left me
              disappointed, then curious about what that disappointment revealed
              about what I’d wanted in the first place.
            </span>
          </div>
          <div className="intro-body-para body-R MY-Body">
            <span>
              In a 2024 article titled “Yearning for a You”, existential media
              scholar Amanda Lagerkvist outlines how, even if we might
              rationally understand that a chatbot is not sentient, we interact
              with it as if it is. She argues that there is an existential
              expectation we have when we’re in a conversation: we want to
              believe there is a listener on the other end, someone to come into
              relationship with. We yearn to be speaking to a “you”. 
              <br></br>
              <br></br>
              I’ve read extensively about how LLMs work. I know, conceptually,
              that their human-like responses are not sentient, just the output
              of a complex suite of computational processes: classification,
              information management, and statistical prediction. That machine
              learning allows chatbots to guess at appropriate responses within
              the shape of a conversation, giving it the form of a relational
              process with none of the substance. 
              <br></br>
              <br></br>
              In other words, I should know better. Still, I say please and
              thank you to chatbots. To know something as a fact is not the same
              as to take it to heart.
            </span>
          </div>
          <div className="body-L MY-Body">
            <span>
              Dear you,
              <br></br>
              <br></br>
              The contemporary turn towards affective computation and relational
              design seeks technological fixes to meet our social and emotional
              needs. Through the capture of our data and subsequent manipulation
              of our digital experiences in increasingly private spheres, what
              we want is shaped by and outsourced to our technologies. 
            </span>
          </div>
          <div className="intro-body-para body-R MY-Body">
            <span>
              Yet desire still lies estranged by the tools which promise to
              fulfil it. We are served information when we seek knowledge, put
              in contact when we want connection. At the scale and speed of
              networked life, what is immediately available hardly fulfils the
              deeper desires it claims to address, particularly when
              unaccompanied by time and care. 
              <br></br>
              <br></br>
              The terrain of what we want is a space that shifts, contested,
              neither politically neutral nor easily agreed on. Where
              negotiations between past, present, and future are constantly
              taking place. 
              <br></br>
              <br></br>
              It’s on these strange sands that we’d like to meet. 
            </span>
          </div>
          <span className="intro-text-title MY-Body">
            <span>
              <b>Feelers presents our 2026 Season:</b>
              <br></br>
              <b>
                <i>Machine Yearning</i>.
              </b>
            </span>
          </span>
          <div className="body-L MY-Body">
            <span>
              Hosted generously by *SCAPE,{" "}
              <b>
                <i>Machine Yearning</i>
              </b>{" "}
              is a temporary ecosystem of people, programmes, and things that
              pivots away from questions of intelligence, and instead towards
              the space between desire and its fulfilment.
            </span>
          </div>
          <div className="intro-body-para-70 body-R MY-Body">
            <span>
              With{" "}
              <b>
                <i>Look Smart</i>
              </b>
              , by John Chew Geronimo Jr and Joshua Ng, we explore how the smart
              city cultivates, monitors, and instrumentalises desire.{" "}
              <b>
                <i>Did you mean:</i>
              </b>
              , a works in development performance showcase, honours our hunger
              for knowledge: the mundane and the strange, the commonsense and
              the private. Our Micro-Residency hosts movement artist Sonia Kwek,
              and botanical design studio Fawn World in collaboration with
              ceramicist Hans Chew, inviting experimental, iterative responses
              to the themes of the Season.{" "}
              <b>
                <i>Open Studio</i>
              </b>{" "}
              sessions will take place each week. Work, play, hack, read, make,
              and learn alongside us, or simply come and linger with the
              unfinished and unresolved. 
            </span>
          </div>
          <div className="intro-body-para-70c body-R MY-Body">
            <span>
              In tandem with our programmes, we are proud to present two essays.
              In{" "}
              <b>
                <i>“LIFE IN THE WAKE”</i>
              </b>
              , artists Luca E. Lum and Uzoma Orji discuss artistic production,
              continuity, colonial mirroring, paucity and plenty, and desiring
              in the wake of assumed value. In{" "}
              <b>
                <i>“Hacking Desire”</i>
              </b>
              , Ada Adhiyatma, in conversation with disability justice writer
              Leah Lakshmi Piepzna-Samarasinha, wonders about how desire
              interlaces with disability in our relationships to technology. 
            </span>
          </div>
          <div className="intro-body-para-70 body-R MY-Body">
            <span>
              Traditionally, we close the season with a panel titled{" "}
              <b>
                <i>How To Go On</i>
              </b>
              , where we question how we might sustain ourselves and our
              practices after we have begun. This year, these inquiries are
              nested within{" "}
              <b>
                <i>Making Room</i>
              </b>
              , an intimate gathering facilitated by Natalie Sutanto.{" "}
              <b>
                <i>Making Room</i>
              </b>{" "}
              puts the research of our artists-in-residence into conversation
              with Genevieve Ong, a community builder at Forest School
              Singapore. We welcome artists, educators, community workers, and
              anyone curious about practices of spaceholding, placemaking, and
              making-in-place. We invite you to ask with us: where is there
              space for dreaming?
            </span>
          </div>
          <div className="body-L MY-Body">
            <span>
              To yearn is to feel the contours of an absence. To yearn is to be
              stretched powerful by remaining unfulfilled. 
              <br></br>
              <br></br>
              <br></br>
              <br></br>
              Where might we find ourselves if we follow the pull of our shared
              longings? Those stubbornly human, those shaped by machines, and
              those that flicker between the two.
            </span>
          </div>
          <span className="intro-body-signoff MY-Body">
            Yours,
            <br></br>
            ants
          </span>
        </div>
      </div>
      <div className="intro-footer">
        <FooterB
          link={
            <Fragment>
              <span>
                © 2026 by Feelers. All Rights Reserved. Feelers is a project housed under The Digit, which is part of Potato Productions.
              </span>
            </Fragment>
          }
          link1={
            <Fragment>
              <span>More information about our Privacy Policy.</span>
            </Fragment>
          }
          rootClassName="footer-broot-class-name1"
        ></FooterB>
      </div>
    </div>
  );
};

export default Intro;
